import React from "react";
import { useTranslation } from "react-i18next";
import { Search, X, Check } from "lucide-react";

import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { DatePicker } from "../../../components/ui/DatePicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { StatusBadge } from "../../../components/ui/StatusBadge";
import { ALL_STATUSES } from "../../../utils/orderStatus";

const SORT_OPTIONS = [
  "created_at:desc",
  "created_at:asc",
  "from_date:asc",
  "from_date:desc",
];

export function OrdersFilters({
  search,
  onSearchChange,
  statuses,
  onStatusesChange,
  fromDate,
  onFromDateChange,
  untilDate,
  onUntilDateChange,
  sort,
  onSortChange,
  onClear,
  isFiltered,
}) {
  const { t } = useTranslation();

  const toggleStatus = (s) => {
    const set = new Set(statuses);
    if (set.has(s)) set.delete(s);
    else set.add(s);
    onStatusesChange([...set]);
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-border-subtle bg-surface p-4 lg:flex-row lg:flex-wrap lg:items-end"
      data-testid="orders-filters"
    >
      {/* Search — full width on small, flex-1 on large */}
      <div className="relative w-full lg:min-w-[260px] lg:flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
        <Input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t("orders.search_placeholder")}
          data-testid="orders-search"
          className="h-10 bg-surface pl-9 border-border-subtle focus-visible:ring-champagne"
        />
      </div>

      {/* Status multi-select via Popover */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("orders.filter_status")}
        </span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-testid="orders-status-trigger"
              className="h-10 min-w-[190px] justify-between border-border-subtle bg-surface text-text-primary hover:bg-offwhite"
            >
              <span className="truncate">
                {statuses.length === 0
                  ? t("orders.filter_status_all")
                  : `${t("orders.filter_status")} (${statuses.length})`}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="max-h-80 w-64 overflow-y-auto border-border-subtle bg-surface p-1"
            data-testid="orders-status-content"
          >
            {ALL_STATUSES.filter((s) => s !== "draft").map((s) => {
              const active = statuses.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  role="menuitemcheckbox"
                  aria-checked={active}
                  onClick={() => toggleStatus(s)}
                  data-testid={`orders-status-option-${s}`}
                  className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                    active ? "bg-offwhite" : "hover:bg-offwhite"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center rounded border border-border-subtle bg-surface">
                    {active && <Check className="h-3 w-3 text-charcoal" />}
                  </span>
                  <StatusBadge status={s} size="sm" />
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => onStatusesChange(["cancelled"])}
              data-testid="orders-status-option-cancelled"
              className={`mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                statuses.includes("cancelled") ? "bg-offwhite" : "hover:bg-offwhite"
              }`}
            >
              <span className="flex h-4 w-4 items-center justify-center rounded border border-border-subtle bg-surface">
                {statuses.includes("cancelled") && (
                  <Check className="h-3 w-3 text-charcoal" />
                )}
              </span>
              <StatusBadge status="cancelled" size="sm" />
            </button>
          </PopoverContent>
        </Popover>
      </div>

      {/* Date range */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("orders.filter_from_date")}
        </span>
        <div className="min-w-[170px]">
          <DatePicker
            value={fromDate || null}
            onChange={(d) => onFromDateChange(d || "")}
            testId="orders-from-date"
          />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("orders.filter_until_date")}
        </span>
        <div className="min-w-[170px]">
          <DatePicker
            value={untilDate || null}
            onChange={(d) => onUntilDateChange(d || "")}
            testId="orders-until-date"
          />
        </div>
      </div>

      {/* Sort */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-text-secondary">
          {t("orders.sort.label")}
        </span>
        <Select value={sort} onValueChange={onSortChange}>
          <SelectTrigger
            className="h-10 min-w-[240px] bg-surface border-border-subtle"
            data-testid="orders-sort-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`orders-sort-${s}`}>
                {t(`orders.sort.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isFiltered && (
        <Button
          variant="ghost"
          onClick={onClear}
          data-testid="orders-clear-filters"
          className="h-10 text-taupe hover:bg-offwhite hover:text-charcoal"
        >
          <X className="mr-1 h-4 w-4" />
          {t("orders.clear_filters")}
        </Button>
      )}
    </div>
  );
}
